/* eslint no-param-reassign: "off", no-console: "off" */

const subscriptionsStore = {
  state: {
    subscription: null,
    subscriptions: [],
    count: 0,
    subscriptionsForAccount: [],
    products: [],
    plans: [],
    metadata: {
      dataPackages: [],
      regions: [],
      mobilityTypes: [],
      satelliteNetworks: [],
      cellularNetworks: []
    },
    subscriptionsLoading: false,
    subscriptionLoading: false,
    subscriptionRefreshing: false,
    subscriptionUsage: null
  },
  mutations: {
    fetchSubscriptionProducts(state, payload) {
      state.products = payload;
    },
    fetchSubscriptionPlans(state, payload) {
      state.plans = payload;
    },
    fetchSubscription(state, payload) {
      state.subscription = payload;
    },
    fetchSubscriptions(state, payload) {
      state.subscriptions = payload.payload ?? [];
      state.count = payload.count ?? 0;
    },
    fetchSubscriptionsForAccount(state, payload) {
      state.fetchSubscriptionsForAccount = payload;
    },
    addSubscription(state, payload) {
      state.subscription = payload;
    },
    editSubscription(state, payload) {
      const tempClone = JSON.parse(JSON.stringify(state.subscription));

      payload.subscription.remoteNames = tempClone.remoteNames;
      state.subscription = payload.subscription;

      const idx = state.subscriptions.findIndex(r => r.id == payload.subscription.id);
      if (idx == -1) return;

      // if the updated Suibscription changed accounts, check to see that the user has permission to the account
      const userAccountMatch = payload.rootState.accounts.accounts.find(a => a.id == payload.subscription.accountId);
      if (!userAccountMatch) { 
        // user does not have permission to the new account, remove the Subscription from the list
        state.subscriptions.splice(idx, 1);
      }
    },
    changeSubscription(state, payload) {
      const tempClone = JSON.parse(JSON.stringify(state.subscription));
      payload.remoteNames = tempClone.remoteNames;
      state.subscription = payload;
    },
    cancelSubscription(state, payload) {
      state.subscription = payload;
    },
    fetchSubscriptionUsage(state, payload) {
      state.subscriptionUsage = payload;
    },
    fetchSubscriptionGroups(state, payload) {
      if (state.subscription) state.subscription.groups = payload;
    },
    cancelPendingSubscriptionTransfer(state) {
      if (state.subscription) state.subscription.pendingTransferEmail = null;
    }
  },
  actions: {
    async fetchSubscriptionProducts({ commit }) {
      try {
        // eslint-disable-next-line no-undef
        const products = $subscriptionConfigStore.getItem('products');
        if (products && products.length) { commit('fetchSubscriptionProducts', products); }
      } catch (ex) { console.error(ex); }

      try {
        const res = await fetch('/v2/info/subscriptionProducts');
        if (res.ok) {
          const data = await res.json();
          commit('fetchSubscriptionProducts', data);
          // eslint-disable-next-line no-undef
          $subscriptionConfigStore.setItem('products', data);
        } else {
          console.error(res);
        }
        return res.ok;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchSubscriptionPlans({ commit }) {
      try {
        // eslint-disable-next-line no-undef
        const plans = $subscriptionConfigStore.getItem('plans');
        if (plans && plans.length) { commit('fetchSubscriptionPlans', plans); }
      } catch (ex) { console.error(ex); }

      try {
        const res = await fetch('/v2/info/subscriptionPlans');
        if (!res.ok) {
          console.error(res);
          return false;
        }
        const data = await res.json();
        commit('fetchSubscriptionPlans', data);
        // eslint-disable-next-line no-undef
        $subscriptionConfigStore.setItem('plans', data);
        return data;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchSubscriptions({ state, commit }, payload) {
      try {
        if (!(payload.bypassState)) state.subscriptionsLoading = true;
        const res = await fetch(`/v2.1/subscriptions?from=${payload.from}&take=${payload.take}&select=${payload.select || ''}&filters=${payload.filters || ''}&sortBy=${payload.sortBy || ''}&sortDirection=${payload.sortDirection || ''}`);
        if (!(payload.bypassState)) state.subscriptionsLoading = false;
        if (!res.ok) {
          throw new Error(await res.text());
        }
        const data = await res.json();
        if (!(payload.bypassState)) commit('fetchSubscriptions', data);
        return data;
      } catch (ex) {
        return { isError: true, ex };
      }
    },
    async fetchSubscription({ state, commit }, payload) {
      state.subscriptionLoading = true;

      if (!payload.bypassCache) {
        // Try to fetch the remote from idxdb first
        try {
          // eslint-disable-next-line no-undef
          const result = await $subscriptionStore.getItem(payload.id);
          // If a remote is found in idxdb
          if (result != null) {
            // Commit it so the page loads
            commit('fetchSubscription', result);
            // Stop the loading
            state.subscriptionLoading = false;
            // Start the refresh
            state.subscriptionRefreshing = true;
          }
        } catch (ex) {
          // If there's an error, failover to API
          console.error(ex);
        }
      }

      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}?includeActivity=${payload.includeActivity || ''}`);
        if (!res.ok) {
          console.error(res);
          return false;
        }
        const data = await res.json();
        commit('fetchSubscription', data);
        state.subscriptionLoading = false;
        state.subscriptionRefreshing = false;
        return data;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchSubscriptionsForAccount({ commit }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/account/${payload.accountId}?active=${payload.active}`);
        if (!res.ok) {
          console.error(res);
          return false;
        }
        const data = await res.json();
        commit('fetchSubscriptionsForAccount', data);
        return data;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    // eslint-disable-next-line no-empty-pattern
    async fetchAllSubscriptionsForAccount({}, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/account/${payload.accountId ? payload.accountId : ''}`);
        if (!res.ok) {
          console.error(res);
          return [];
        }
        return await res.json();
      } catch (ex) {
        console.error(ex);
        return [];
      }
    },
    async editSubscription({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}/edit`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        // catch failed request
        if (!res.ok) throw new Error(await res.text());
        // extract updated Subscription
        const subscription = await res.json(); 
        // commit to vuex state
        commit('editSubscription', { rootState, subscription });
        // return updated subscription
        return subscription;
      } catch (err) {
        console.error(err);
        return { isError: true, err };
      }
    },
    async transferSubscription({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}/transfer`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        const subscription = await res.json(); 
        commit('editSubscription', { rootState, subscription });
        return { isSuccess: true, subscription };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, error: ex.message };
      }
    },
    async cancelPendingSubscriptionTransfer({ commit, rootState }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}/transfer`, {
          method: 'DELETE',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) throw new Error(await res.text());
        const updated = await res.json(); 
        commit('editSubscription', { rootState, subscription: updated });
        commit('cancelPendingSubscriptionTransfer', {});
        return { isSuccess: true, updatedSubscription: updated };
      } catch (ex) {
        console.error(ex);
        return { isSuccess: false, error: ex.message };
      }
    },
    async changeSubscription({ commit }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}`, {
          body: JSON.stringify(payload),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) {
          try { 
            return { success: res.ok, data: await res.text() }; // 400, text error message
          } catch (ex) { 
            return { success: res.ok, data: await res.json() }; // 500, JSON object
          } 
        }
        const data = await res.json();
        commit('changeSubscription', data);
        return { success: res.ok, data };
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async addSubscription({ commit }, payload) {
      try {
        const res = await fetch('/v2.1/subscriptions', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) {
          try {
            return { success: res.ok, data: await res.text() }; // 400, text error message
          } catch (ex) {
            return { success: res.ok, data: await res.json() }; // 500, JSON object
          } 
        }
        // success
        const data = await res.json(); 
        commit('addSubscription', data);
        return { success: res.ok, data };
      } catch (ex) {
        console.error(ex);
        return ex;
      }
    },
    async cancelSubscription({ dispatch }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}`, {
          method: 'DELETE'
        });
        if (!res.ok) {
          try {
            return await res.text(); // 400, text error message
          } catch (ex) {
            return await res.json(); // 500, JSON object
          } 
        }
        const data = await res.text(); 
        if (data) {
          await dispatch('fetchSubscription', { id: payload.id });
          return true;
        }
        return false;
      } catch (ex) {
        console.error(ex);
        return ex;
      }
    },
    async updateSubscriptionPool({ dispatch }, payload) {
      try {
        const res = await fetch(`/v2.1/subscriptions/${payload.id}/pool`, {
          body: JSON.stringify(payload.remotes),
          method: 'PUT',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        if (!res.ok) {
          try {
            return { success: false, data: await res.text() }; // 400, text error message
          } catch (ex) {
            return { success: false, data: await res.json() }; // 500, JSON object
          }
        }
        
        const result = await dispatch('fetchSubscription', { id: payload.id });
        return { success: true, data: result };
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async clearSubscriptionCache() {
      // eslint-disable-next-line no-undef
      await $subscriptionStore.clear();
      return true;
    },
    // eslint-disable-next-line no-empty-pattern
    async fetchSubscriptionReport({}, payload) {
      try {
        const res = await fetch(`/v2/subscriptions/${payload.getUTCFullYear()}/${payload.getUTCMonth() + 1}`);
        if (!res.ok) throw new Error(await res.text());
        const blob = await res.blob();
        return blob;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    },
    async fetchSubscriptionUsage({ commit }, payload) {
      try {
        const res = await fetch('/v2.1/subscriptions/usage', {
          body: JSON.stringify(payload),
          method: 'POST',
          headers: new Headers({ 'Content-Type': 'application/json' })
        });
        // check for failed response
        if (!res.ok) throw new Error(await res.text());
        const usageData = await res.json();
        commit('fetchSubscriptionUsage', usageData);
        // response indicates success, extract usage data as JSON
        return { success: true, data: usageData };
      } catch (ex) {
        console.error(ex);
        return { 
          success: false, 
          data: `Failed to fetch Subscription usage due to exception: ${ex}`
        };
      }
    },
    async fetchSubscriptionGroups({ commit }, payload) {
      try {
        const res = await fetch(`/v2/groups/entities/${payload.entityId}?entityType=${payload.entityType}`);
        if (!res.ok) throw new Error(await res.text());
        const groupsData = await res.json();
        commit('fetchSubscriptionGroups', groupsData);
        return groupsData;
      } catch (ex) {
        console.error(ex);
        return false;
      }
    }
  },
  getters: {

  }
}
export default subscriptionsStore;
